import {
  readPrivateJson,
  PrivateReadHttpError,
} from "../../redacted/v2/readJson";
import type { FeeAssetMetadata } from "./privateFeeAmounts";

const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/** Use the chain's denomination units, never the underlying token's precision
 * or a guessed 1:1 exchange rate. Some Rujira receipts have display=base while
 * their human unit is registered separately under symbol (e.g. sRUJI). */
export const parsePrivateFeeMetadata = (
  denom: string,
  body: unknown
): FeeAssetMetadata | undefined => {
  const metadata = record(record(body)?.metadata);
  if (
    !metadata ||
    metadata.base !== denom ||
    typeof metadata.symbol !== "string" ||
    !metadata.symbol.trim() ||
    metadata.symbol.length > 128 ||
    /[\u0000-\u001f\u007f-\u009f]/.test(metadata.symbol) ||
    !Array.isArray(metadata.denom_units)
  )
    return undefined;
  const units = metadata.denom_units.map(record);
  const base = units.filter((unit) => unit?.denom === denom);
  const symbol = units.filter((unit) => unit?.denom === metadata.symbol);
  const decimals = symbol[0]?.exponent;
  if (
    base.length !== 1 ||
    base[0]?.exponent !== 0 ||
    symbol.length !== 1 ||
    typeof decimals !== "number" ||
    !Number.isInteger(decimals) ||
    decimals < 0 ||
    decimals > 30
  )
    return undefined;
  return {
    metadata: { symbol: metadata.symbol, decimals },
    variants: { native: { denom } },
  };
};

/** Public metadata only: no account, amount, proof or wallet identifier. A
 * failed lookup leaves the exact raw-unit fallback; it cannot affect a quote. */
export const readPrivateFeeMetadata = async (
  lcdUrl: string,
  denom: string,
  fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args)
): Promise<FeeAssetMetadata | null> => {
  const result = await readPrivateJson(
    fetchImpl,
    // Query-string endpoint preserves nested receipt denominations without
    // encoded path separators being rejected by a reverse proxy.
    `${lcdUrl.replace(/\/$/, "")}/cosmos/bank/v1beta1/denoms_metadata_by_query_string?denom=${encodeURIComponent(denom)}`
  );
  if (result.status === 404) return null;
  if (!result.ok)
    throw new PrivateReadHttpError("Token metadata unavailable", result.status);
  return parsePrivateFeeMetadata(denom, result.body) ?? null;
};
